import { useCallback, useEffect, useState } from "react";
import api, { API_ORIGIN } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";

// Read-only data hooks — the frontend is a pure consumer of PPE + Social APIs.

async function safeGet(path) {
  try {
    const { data } = await api.get(path);
    return Array.isArray(data) ? data : data || [];
  } catch {
    return [];
  }
}

// Aggregates connection health from e-commerce sources + social accounts.
export function useConnectionHealth() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ sources: [], accounts: [], loading: true });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [sources, accounts] = await Promise.all([
      safeGet("/promotions/sources"),
      safeGet("/social/accounts"),
    ]);
    setState({ sources, accounts, loading: false });
  }, []);

  useEffect(() => {
    if (currentOrg) load();
  }, [currentOrg, load]);

  return { ...state, reload: load };
}

// Promotions list — used for Home overview KPIs (read-only).
export function usePromotions() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ promotions: [], loading: true });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const promotions = await safeGet("/promotions");
    setState({ promotions, loading: false });
  }, []);

  useEffect(() => {
    if (currentOrg) load();
  }, [currentOrg, load]);

  return { ...state, reload: load };
}

// All static/config data the New Campaign wizard consumes (read-only).
export function useWizardData() {
  const { currentOrg } = useOrg();
  const [data, setData] = useState({
    sources: [], accounts: [], kbs: [], providers: [], prompts: [],
    strategies: [], modes: [], loading: true,
  });

  useEffect(() => {
    if (!currentOrg) return;
    let alive = true;
    (async () => {
      const [sources, accounts, kbs, provResp, prompts, strat] = await Promise.all([
        safeGet("/promotions/sources"),
        safeGet("/social/accounts"),
        safeGet("/knowledge/bases"),
        api.get("/promotions/ai/providers").then((r) => r.data).catch(() => ({ providers: [] })),
        safeGet("/ai/prompts"),
        api.get("/promotions/strategies").then((r) => r.data).catch(() => ({})),
      ]);
      if (!alive) return;
      setData({
        sources,
        accounts: accounts.filter((a) => a.status === "connected"),
        kbs,
        providers: provResp.providers || [],
        prompts: (prompts || []).filter((p) => String(p.key).startsWith("promotion.")),
        strategies: strat.selection_strategies || [],
        modes: strat.generation_modes || [],
        loading: false,
      });
    })();
    return () => { alive = false; };
  }, [currentOrg]);

  return data;
}

// Fetch synced products for a source platform (read-only catalog preview).
export async function fetchProducts(platform) {
  const q = platform ? `?platform=${encodeURIComponent(platform)}` : "";
  return safeGet(`/ecommerce/products${q}`);
}

// Executive Dashboard aggregate (read-only) — promotions + promotion posts + events + accounts.
export function useDashboardData() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ promotions: [], posts: [], events: [], accounts: [], loading: true });

  const load = useCallback(async () => {
    const [promotions, allPosts, allEvents, accounts] = await Promise.all([
      safeGet("/promotions"),
      safeGet("/social/posts"),
      safeGet("/events?limit=120"),
      safeGet("/social/accounts"),
    ]);
    const posts = (allPosts || []).filter((p) => p.source === "promotion");
    const events = (allEvents || []).filter((e) => /^(promotion|campaign|social\.post)/.test(e.type || ""));
    setState({ promotions, posts, events, accounts, loading: false });
  }, []);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);
  return { ...state, reload: load };
}


// Global operational queue: aggregates promotion_items across all campaigns + linked posts + events.
export function useQueueData() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ rows: [], events: [], promotions: [], loading: true });

  const load = useCallback(async () => {
    const promotions = await safeGet("/promotions");
    const [itemsArrays, allPosts, events] = await Promise.all([
      Promise.all(promotions.map((p) => api.get(`/promotions/${p.id}/items`).then((r) => r.data).catch(() => []))),
      safeGet("/social/posts"),
      safeGet("/events?limit=200"),
    ]);
    const postsById = Object.fromEntries((allPosts || []).map((p) => [p.id, p]));
    const rows = [];
    promotions.forEach((promo, i) => {
      (itemsArrays[i] || []).forEach((item) => {
        rows.push({ item, promo, post: postsById[item.social_post_id] || null });
      });
    });
    setState({ rows, events, promotions, loading: false });
  }, []);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);
  return { ...state, reload: load };
}

// Read-only aggregate for the Promotion Center Settings console (existing endpoints only).
export function useSettingsData() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({
    promotions: [], sources: [], accounts: [], posts: [], events: [],
    providers: [], prompts: [], kbs: [], stats: null, health: null, loading: true,
  });

  const load = useCallback(async () => {
    setState((s) => ({ ...s, loading: true }));
    const [promotions, sources, accounts, allPosts, events, provResp, prompts, kbs, stats, health] = await Promise.all([
      safeGet("/promotions"),
      safeGet("/promotions/sources"),
      safeGet("/social/accounts"),
      safeGet("/social/posts"),
      safeGet("/events?limit=50"),
      api.get("/promotions/ai/providers").then((r) => r.data).catch(() => ({ providers: [] })),
      safeGet("/ai/prompts"),
      safeGet("/knowledge/bases"),
      api.get("/ecommerce/stats").then((r) => r.data).catch(() => null),
      api.get(`${API_ORIGIN}/api/health`).then((r) => r.data).catch(() => null),
    ]);
    setState({
      promotions,
      sources,
      accounts,
      posts: (allPosts || []).filter((p) => p.source === "promotion"),
      events: (events || []).filter((e) => /^(promotion|campaign|social\.post)/.test(e.type || "")),
      providers: provResp.providers || [],
      prompts: (prompts || []).filter((p) => String(p.key).startsWith("promotion.")),
      kbs,
      stats,
      health,
      loading: false,
    });
  }, []);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);
  return { ...state, reload: load };
}


// Full activity timeline: events + lookup maps to reconstruct campaign/product/platform.
export function useTimelineData() {
  const { currentOrg } = useOrg();
  const [state, setState] = useState({ events: [], promoById: {}, promoByCampaign: {}, itemByPostId: {}, loading: true });

  const load = useCallback(async () => {
    const promotions = await safeGet("/promotions");
    const [itemsArrays, allEvents] = await Promise.all([
      Promise.all(promotions.map((p) => api.get(`/promotions/${p.id}/items`).then((r) => r.data).catch(() => []))),
      safeGet("/events?limit=300"),
    ]);
    const promoById = Object.fromEntries(promotions.map((p) => [p.id, p]));
    const promoByCampaign = Object.fromEntries(promotions.filter((p) => p.social_campaign_id).map((p) => [p.social_campaign_id, p]));
    const itemByPostId = {};
    promotions.forEach((p, i) => (itemsArrays[i] || []).forEach((it) => {
      if (it.social_post_id) itemByPostId[it.social_post_id] = { promo: p, product: it.product_external_id, platform: it.platform };
    }));
    const events = (allEvents || []).filter((e) => /^(promotion|campaign|social\.post)/.test(e.type || ""));
    setState({ events, promoById, promoByCampaign, itemByPostId, loading: false });
  }, []);

  useEffect(() => { if (currentOrg) load(); }, [currentOrg, load]);
  return { ...state, reload: load };
}
